import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { TagModule } from 'primeng/tag';
import { PermissionService } from '@app/core/services/permission.service';
import { FlashSaleService } from '@app/feature/center-business/marketing/flash-sale/services/flash-sale.service';
import type { FlashSaleQuotaPolicy } from '@app/api/price-service/flash-sale-api.service';

@Component({
  selector: 'app-flash-sale-quota-policy',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ToastModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    TagModule,
  ],
  providers: [MessageService],
  templateUrl: './flash-sale-quota-policy.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlashSaleQuotaPolicyComponent implements OnInit {
  private readonly api = inject(FlashSaleService);
  private readonly messages = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly permissions = inject(PermissionService);

  readonly items = signal<FlashSaleQuotaPolicy[]>([]);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly dialogOpen = signal(false);
  readonly editingId = signal<number | null>(null);

  name = '';
  maxStockPercent: number | null = 50;
  activeFlag = true;

  readonly canCreate = () =>
    this.permissions.canDoAction('settings.price.flash_sale_quota.create');
  readonly canEdit = () =>
    this.permissions.canDoAction('settings.price.flash_sale_quota.edit');
  readonly canDelete = () =>
    this.permissions.canDoAction('settings.price.flash_sale_quota.delete');

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.api.listQuotaPolicies().subscribe({
      next: (items) => {
        this.items.set(items ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('flash_sale_quota.errors.load'),
        });
      },
    });
  }

  openCreate(): void {
    this.editingId.set(null);
    this.name = '';
    this.maxStockPercent = 50;
    this.activeFlag = true;
    this.dialogOpen.set(true);
  }

  openEdit(item: FlashSaleQuotaPolicy): void {
    this.editingId.set(item.id);
    this.name = item.name;
    this.maxStockPercent = item.maxStockPercent;
    this.activeFlag = item.active;
    this.dialogOpen.set(true);
  }

  save(): void {
    if (!this.name.trim() || this.maxStockPercent == null) {
      return;
    }
    const body = {
      name: this.name.trim(),
      maxStockPercent: this.maxStockPercent,
      activeFlag: this.activeFlag,
    };
    this.saving.set(true);
    const id = this.editingId();
    const req$ =
      id == null ? this.api.createQuotaPolicy(body) : this.api.updateQuotaPolicy(id, body);
    req$.subscribe({
      next: () => {
        this.saving.set(false);
        this.dialogOpen.set(false);
        this.reload();
        this.messages.add({
          severity: 'success',
          summary: this.translate.instant('flash_sale_quota.saved'),
        });
      },
      error: () => {
        this.saving.set(false);
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('flash_sale_quota.errors.save'),
        });
      },
    });
  }

  remove(item: FlashSaleQuotaPolicy): void {
    this.api.deleteQuotaPolicy(item.id).subscribe({
      next: () => this.reload(),
      error: () =>
        this.messages.add({
          severity: 'error',
          summary: this.translate.instant('flash_sale_quota.errors.delete'),
        }),
    });
  }
}
